<script setup lang="ts">
import { useDrawerStore } from "@/stores";
import { useScreenSize } from "@soft-db/ui";

const props = defineProps<{
	forceOpen?: boolean;
	defaultOpen?: boolean;
	/**
	 * Disabled the drawer, forcing the closing state
	 */
	disabled?: boolean;
	mobileOnly?: boolean;
}>();

const drawerStore = useDrawerStore();

const stateChanged = ref(false);
watchOnce(
	() => drawerStore.open,
	() => {
		stateChanged.value = true;
	}
);

const { mdOrLower } = useScreenSize();

const desktopOpen = computed(() => {
	if (props.forceOpen) {
		return true;
	}

	if (props.mobileOnly && !mdOrLower.value) {
		return false;
	}

	if (props.disabled) {
		return false;
	}

	return drawerStore.open;
});

const mobileOpen = toRef(drawerStore, "mobileOpen");

onMounted(() => {
	if (mdOrLower.value && props.forceOpen && !drawerStore.open) {
		drawerStore.toggleDrawer();
	}
});
</script>

<template>
	<template v-if="mdOrLower">
		<div
			class="fixed bottom-0 left-0 top-0 z-20 transition-transform duration-300"
			:class="mdOrLower && !mobileOpen ? '-translate-x-80' : ''"
		>
			<slot name="drawer" />
		</div>
		<div
			class="dialog-overlay z-10! transition-opacity duration-300"
			:class="!mobileOpen && 'pointer-events-none opacity-0'"
			@click="drawerStore.toggleDrawer"
		/>
		<slot />
	</template>
	<div
		v-else
		:class="{
			'grid size-full grid-cols-[0px_1fr] transition-[grid-template-columns] duration-500': true,
			'grid-cols-[287px_1fr]': desktopOpen,
		}"
	>
		<div
			:class="{
				'relative w-[287px] -translate-x-full overflow-clip border-r transition-transform duration-500': true,
				'translate-x-0': desktopOpen,
			}"
		>
			<div class="absolute size-full">
				<slot name="drawer" />
			</div>
		</div>
		<slot />
	</div>
</template>
